/**
 * Preparation Requests API — CRUD for the Supabase preparation_requests table.
 *
 * A preparation request is a "notebook" the Adviser fills in before a client
 * appointment, listing the tasks the New Business Assistant must complete. It
 * is a SEPARATE record type from `files` (see CLAUDE.md §25) and must never be
 * mixed into file stats, the bonus counter, or Avg. Days.
 *
 * Phase 3 only needs create (the Adviser submission form). Reads/updates for
 * the shared pipeline arrive in a later phase.
 */

import supabase from './supabaseClient';

/**
 * Map a Supabase row (snake_case) to the internal camelCase shape.
 */
function mapRowToPrepRequest(row) {
  return {
    id: row.id,
    clientName: row.client_name || '',
    appointmentDate: row.appointment_date || null,
    tasks: Array.isArray(row.tasks) ? row.tasks : [],
    status: row.status || 'submitted',
    createdAt: row.created_at || null,
    startedAt: row.started_at || null,
    completedAt: row.completed_at || null,
    createdBy: row.created_by || '',
    clientLanguagePreference: row.client_language_preference || 'English',
    priority: row.priority || 'normal',
    writtenInstructions: row.written_instructions || null,
    // Test-record marker (migration 23). Set only by the Adviser via the
    // "Load as test file" checkbox at submission; defaults to false. A
    // follow-up change will exclude test records from stats and pipeline
    // rules — nothing reads this yet.
    isTest: row.is_test === true,
    // Review-handoff linkage (CLAUDE.md §34.11). A prep request created
    // from a review meeting records where it came from; group requests
    // additionally carry the per-member segmentation. Shape of
    // member_sections (app-defined; jsonb):
    //   { groupLabel, reviewMonth, reviewYear, voiceNoteUrl,
    //     sections: [{ reviewClientId, name, isEntity, notes, prepared }] }
    // Ordinary prep requests keep all three null and behave exactly as
    // before.
    sourceReviewGroupId: row.source_review_group_id || null,
    sourceReviewClientId: row.source_review_client_id || null,
    memberSections: row.member_sections || null,
    // Adviser's spoken note for this request (migration 24), attached
    // as-is. `voice_note_transcript` is DEPRECATED (29 September 2026):
    // read-only historical data from the removed Whisper pipeline, never
    // written or displayed any more. No summary exists for prep requests.
    voiceNoteUrl: row.voice_note_url || null,
    voiceNoteTranscript: row.voice_note_transcript || null,
    // Task-amendment acknowledgement (migration 25, CLAUDE.md §26.8).
    // Unacknowledged ⇔ amendedAt set AND (ackAt null OR ackAt < amendedAt) —
    // see hasUnacknowledgedAmendment() in utils/prepRequests.js.
    tasksAmendedAt: row.tasks_amended_at || null,
    tasksAmendmentAckAt: row.tasks_amendment_ack_at || null,
    // Closed preparation rounds (migration 26, CLAUDE.md 26.10) -- append-only
    // history, snapshotted when the Adviser starts a new round. The CURRENT
    // round lives in the ordinary columns above. Prior rounds are permanent
    // records: never edited, reordered, deleted or restated.
    priorRounds: Array.isArray(row.prior_rounds) ? row.prior_rounds : [],
  };
}

/**
 * Create a new preparation request.
 *
 * @param {object} data
 * @param {string} data.clientName       — trimmed client name
 * @param {string} data.appointmentDate  — 'YYYY-MM-DD'
 * @param {Array}  data.tasks            — [{ id, text, completed }]
 * @param {string} data.createdBy        — the ORIGINATOR's email: whoever loaded
 *                                       the request, the Adviser or the
 *                                       Administration Assistant (§26.1).
 *                                       Mirrors files.submitted_by.
 * @returns {Promise<object>} the created prep request (camelCase)
 */
export async function createPrepRequest(data) {
  const row = {
    client_name: data.clientName,
    appointment_date: data.appointmentDate,
    tasks: Array.isArray(data.tasks) ? data.tasks : [],
    status: 'submitted',
    created_by: data.createdBy || null,
    client_language_preference: data.clientLanguagePreference || 'English',
    priority: data.priority || 'normal',
    written_instructions: data.writtenInstructions || null,
    // Test-record marker — strictly boolean, defaults to false so normal
    // submissions are always real records (migration 23).
    is_test: data.isTest === true,
    // Review-handoff linkage (§34.11) — null for ordinary submissions.
    source_review_group_id: data.sourceReviewGroupId || null,
    source_review_client_id: data.sourceReviewClientId || null,
    member_sections: data.memberSections || null,
    // Voice note (migration 24) — null when the adviser submitted without
    // recording one. voice_note_transcript is deprecated and never written.
    voice_note_url: data.voiceNoteUrl || null,
  };

  const { data: inserted, error } = await supabase
    .from('preparation_requests')
    .insert(row)
    .select()
    .single();

  if (error) throw new Error(`Failed to create preparation request: ${error.message}`);
  return mapRowToPrepRequest(inserted);
}

/**
 * Fetch all preparation requests (newest first). Read-only; used by the
 * shared pipeline and the prep-request detail/stats views.
 */
export async function getAllPrepRequests() {
  const { data, error } = await supabase
    .from('preparation_requests')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) throw new Error(`Failed to load preparation requests: ${error.message}`);
  return (data || []).map(mapRowToPrepRequest);
}

/**
 * Update fields on a preparation request (New Business Assistant actions:
 * status transitions and task toggles). Accepts a camelCase patch and maps
 * only the recognised fields to the DB columns, so callers can't accidentally
 * touch immutable fields (client_name, appointment_date, created_by).
 *
 * Recognised: status, startedAt → started_at, completedAt → completed_at
 * (null clears the timestamp on a revert), tasks (the full JSONB array),
 * memberSections → member_sections (the per-member "prepared" ticks on
 * segmented review-handoff requests, §34.11 — a read-modify-write of the
 * whole object, same pattern as tasks).
 */
export async function updatePrepRequest(id, patch = {}) {
  const row = {};
  if ('status' in patch) row.status = patch.status;
  if ('startedAt' in patch) row.started_at = patch.startedAt;
  if ('completedAt' in patch) row.completed_at = patch.completedAt;
  if ('tasks' in patch) row.tasks = patch.tasks;
  if ('memberSections' in patch) row.member_sections = patch.memberSections;
  if ('tasksAmendedAt' in patch) row.tasks_amended_at = patch.tasksAmendedAt;
  if ('tasksAmendmentAckAt' in patch) row.tasks_amendment_ack_at = patch.tasksAmendmentAckAt;
  // Rounds (26.10). appointmentDate is otherwise immutable after submission --
  // it is accepted here ONLY for the Adviser's start-new-round action, which
  // sets the new round's deadline anchor; priorRounds is the append-only
  // history that same action snapshots into.
  if ('priorRounds' in patch) row.prior_rounds = patch.priorRounds;
  if ('appointmentDate' in patch) row.appointment_date = patch.appointmentDate;

  const { data, error } = await supabase
    .from('preparation_requests')
    .update(row)
    .eq('id', id)
    .select()
    .single();

  if (error) throw new Error(`Failed to update preparation request: ${error.message}`);
  return mapRowToPrepRequest(data);
}

/**
 * Hard-delete a preparation request (Adviser only).
 *
 * Mirrors the client-file hard delete (filesApi.deleteFile): the row is
 * removed from the database. The DELETE RLS policy (migration 15) restricts
 * this to the Adviser, so a non-Adviser attempt is rejected server-side.
 * files.source_prep_sheet_id is ON DELETE SET NULL, so any file pre-populated
 * from this sheet keeps intact — only its back-link clears.
 */
export async function deletePrepRequest(id) {
  const { error } = await supabase
    .from('preparation_requests')
    .delete()
    .eq('id', id);

  if (error) throw new Error(`Failed to delete preparation request: ${error.message}`);
}

/**
 * Fetch a single preparation request by id (read-only detail view).
 */
export async function getPrepRequestById(id) {
  const { data, error } = await supabase
    .from('preparation_requests')
    .select('*')
    .eq('id', id)
    .single();

  if (error) throw new Error(`Failed to load preparation request: ${error.message}`);
  return mapRowToPrepRequest(data);
}
